import type { AdminSendOutcome, KycVerdict, StaffCandidate } from "../api/types";
import { formatDateTime } from "../format";

const KYC_CHIP: Record<
  "approved" | "rejected" | "pending",
  { label: string; className: string }
> = {
  approved: { label: "Approved", className: "chip chip-ok" },
  pending: { label: "Pending", className: "chip chip-neutral" },
  rejected: { label: "Rejected", className: "chip chip-bad" },
};

/**
 * KYC verdict as served. `null` is not a failure: it means the user has not
 * started verification, which is why it reads "Not started" and stays muted.
 *
 * `prefix` names the chip where no column header does, in a phone's list row.
 */
export function KycChip({ status, prefix = "" }: { status: KycVerdict; prefix?: string }) {
  if (status === null) return <span className="chip chip-muted">{prefix}Not started</span>;
  const chip = KYC_CHIP[status];
  return (
    <span className={chip.className}>
      {prefix}
      {chip.label}
    </span>
  );
}

export function BoolChip({
  value,
  yes,
  no,
}: {
  value: boolean;
  yes: string;
  no: string;
}) {
  return (
    <span className={value ? "chip chip-ok" : "chip chip-muted"}>
      {value ? yes : no}
    </span>
  );
}

const ROLE_CHIP: Record<StaffCandidate["role"], string> = {
  admin: "chip chip-accent",
  rm: "chip chip-neutral",
  user: "chip chip-neutral",
};

/**
 * A role as the API serves it, uppercased for display only: the wire values
 * stay lowercase everywhere they are sent back.
 *
 * Both staff roles are solid chips. The dashed `chip-muted` means an absent
 * value ("Not started", "no code"), and using it for RM made the same control
 * look like two different kinds of thing depending on what was selected.
 */
export function RoleChip({ role }: { role: StaffCandidate["role"] }) {
  return <span className={ROLE_CHIP[role]}>{role.toUpperCase()}</span>;
}

/**
 * Whether a sent notification has been opened, and when. `short` drops the
 * time for a phone's list row, which says it on its meta line instead.
 */
export function ReadChip({ item, short = false }: { item: AdminSendOutcome; short?: boolean }) {
  return item.read ? (
    <span className="chip chip-ok">
      Read{item.readAt && !short ? ` ${formatDateTime(item.readAt)}` : ""}
    </span>
  ) : (
    <span className="chip chip-muted">Unread</span>
  );
}
